import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import GetAppIcon from '@mui/icons-material/GetApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import { supabase } from '../utils/supabaseClient';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Chip from '@mui/material/Chip';
import { initializePDF, addSectionHeader, formatCurrency, getTableStyles, PDF_STYLES_CONSTANTS } from '../utils/pdfUtils';
import { format } from 'date-fns';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Snackbar from '@mui/material/Snackbar';
import SortableTable from '../components/SortableTable';
import BulkExportDialog from '../components/BulkExportDialog';
import { escapeCSVField } from '../utils/bulkExportUtils';

// Helper function for consistent date handling
const getAdjustedDate = (dateInput) => {
  // Handle if input is already a Date object
  if (dateInput instanceof Date) {
    const date = new Date(dateInput);
    date.setUTCHours(12, 0, 0, 0);
    return date;
  }
  
  // Handle string input
  if (typeof dateInput === 'string') {
    // If it's already an ISO string with time, use it as is
    if (dateInput.includes('T')) {
      return new Date(dateInput);
    }
    // Otherwise append noon UTC time to ensure date consistency
    return new Date(`${dateInput}T12:00:00Z`);
  }
  
  // Return null for undefined or null input
  return null;
};

function Results() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allResults, setAllResults] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalLocations, setTotalLocations] = useState(0);
  const [totalBoards, setTotalBoards] = useState(0);
  const [activeProvider, setActiveProvider] = useState(0);
  const [error, setError] = useState('');
  const [showCalculateButton, setShowCalculateButton] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null);
  const [cocaColaRevenue, setCocaColaRevenue] = useState(0);
  const [netProfit, setNetProfit] = useState(0);
  const [isRefusalCalculation, setIsRefusalCalculation] = useState(false);
  const [recentCalculations, setRecentCalculations] = useState([]);
  const [archivedCalculations, setArchivedCalculations] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [fileToArchive, setFileToArchive] = useState(null);
  const [fileToRestore, setFileToRestore] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState({});
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResults, setEmailResults] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [bulkExportDialogOpen, setBulkExportDialogOpen] = useState(false);
  const [hasHourlyWork, setHasHourlyWork] = useState(false);
  const [serviceTypeSummary, setServiceTypeSummary] = useState({});
  
  useEffect(() => {
    if (resultId) {
      fetchResultData();
    } else {
      fetchRecentCalculations();
      fetchArchivedCalculations();
    }
  }, [resultId]);

  const fetchRecentCalculations = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('uploads')
        .select('id, filename, created_at, status, file_path, provider_count, total_amount, uploaded_by, work_week_from_date, work_week_to_date, is_archived')
        .in('status', ['processed', 'completed'])
        .eq('is_archived', false)
        .order('created_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      setRecentCalculations(data || []);
    } catch (error) {
      console.error('Error fetching recent calculations:', error);
      setError('Failed to load recent calculations: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedCalculations = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('uploads')
        .select('id, filename, created_at, status, file_path, provider_count, total_amount, uploaded_by, work_week_from_date, work_week_to_date, is_archived, archived_at')
        .in('status', ['processed', 'completed'])
        .eq('is_archived', true)
        .order('archived_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      setArchivedCalculations(data || []);
    } catch (error) {
      console.error('Error fetching archived calculations:', error);
      setError('Failed to load archived calculations: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchResultData = async () => {
    try {
      setLoading(true);
      
      // First, get the upload_id from the most recent set of calculations if no ID specified
      let uploadIdQuery;
      
      if (resultId) {
        // The resultId from the URL is actually the upload_id
        uploadIdQuery = resultId;
        
        // Verify this upload exists and has been processed
        const { data: uploadData, error: uploadError } = await supabase
          .from('uploads')
          .select('status, file_path, work_week_from_date, work_week_to_date')
          .eq('id', resultId)
          .single();
        
        if (uploadError) {
          if (uploadError.code === 'PGRST116') {
            setError('Upload not found. The file may have been deleted.');
          } else {
            throw uploadError;
          }
          setLoading(false);
          return;
        }
        
        if (!uploadData) {
          setError('Upload not found. The file may have been deleted.');
          setLoading(false);
          return;
        }
        
        if (uploadData.status !== 'processed') {
          setError(`This file has not been processed yet (status: ${uploadData.status}). Please process it first.`);
          setLoading(false);
          setShowCalculateButton(true);
          return;
        }
      } else {
        // Get the most recent processed upload
        const { data: latestUpload, error: uploadError } = await supabase
          .from('uploads')
          .select('id')
          .eq('status', 'processed')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
          
        if (uploadError) {
          console.error('Error fetching recent upload:', uploadError);
          setError('No processed files found. Please upload and process a file first.');
          setLoading(false);
          return;
        }
        
        if (!latestUpload) {
          setError('No processed files found. Please upload and process a file first.');
          setLoading(false);
          return;
        }
        
        uploadIdQuery = latestUpload.id;
      }
      
      // Now get all calculation results for this upload
      const { data: calcResults, error: calcError } = await supabase
        .from('calculation_results')
        .select(`
          id,
          upload_id,
          provider_id,
          location_count,
          total_units,
          total_cost,
          pay_date,
          created_at,
          service_type_id,
          service_type_name,
          rate_type,
          billable_hours,
          hourly_rate,
          providers (
            id,
            name,
            net_terms,
            email,
            hourly_rate
          ),
          uploads (
            file_path,
            work_week_from_date,
            work_week_to_date,
            has_hourly_work,
            service_types_found
          )
        `)
        .eq('upload_id', uploadIdQuery)
        .order('created_at', { ascending: false });
        
      if (calcError) throw calcError;
      
      if (!calcResults || calcResults.length === 0) {
        if (resultId) {
          // This is a specific upload that was marked as processed but has no calculation results
          // This typically happens when no providers in the file matched any active providers in the Rate Table
          setError(`NO_RESULTS:${resultId.substring(0, 8)}`);
        } else {
          setError('No calculation results found. Please upload and process a file first.');
        }
        setLoading(false);
        return;
      }
      
      // For each calculation result, get its location results
      const providersWithLocations = [];
      let grandTotalAmount = 0;
      let grandTotalLocations = 0;
      let grandTotalBoards = 0;
      let hasHourlyWork = false;
      
      for (const calc of calcResults) {
        console.log('Fetching locations for calculation:', calc.id);
        const { data: locations, error: locError } = await supabase
          .from('location_results')
          .select('*')
          .eq('calculation_id', calc.id);
          
        if (locError) {
          console.error('Error fetching locations:', locError);
          throw locError;
        }
        
        console.log(`Found ${locations?.length || 0} locations for calc ${calc.id}:`, locations);
        // Debug hourly values per row
        if (locations && locations.length > 0) {
          locations.forEach((loc, idx) => {
            console.debug('[DBG] location_results row', {
              idx,
              order_numbers: loc.order_numbers,
              rate_type: loc.rate_type,
              travel_hours: loc.travel_hours,
              onsite_hours: loc.onsite_hours,
              billable_hours: loc.billable_hours
            });
          });
        }
        
        // Handle empty locations array
        if (!locations || locations.length === 0) {
          console.warn(`No locations found for calculation ${calc.id} - provider: ${calc.providers?.name}`);
        }
        
        // Check if any location has hourly work
        const hasHourlyLocations = locations && locations.length > 0 
          ? locations.some(loc => 
              // Prefer explicit rate_type when available
              loc.rate_type === 'HOURLY' ||
              // Be resilient: show columns if we have any hour data
              (Number(loc.billable_hours) > 0) ||
              (Number(loc.travel_hours) > 0) ||
              (Number(loc.onsite_hours) > 0)
            )
          : false;
        if (hasHourlyLocations) hasHourlyWork = true;
        
        // Format this provider's results
        const formattedResult = {
          id: calc.id,
          provider_id: calc.provider_id,
          provider_name: calc.providers?.name || 'Unknown Provider',
          provider_email: calc.providers?.email || '',
          provider_hourly_rate: calc.providers?.hourly_rate || 0,
          net_terms: calc.providers?.net_terms || null,
          calculation_date: calc.created_at,
          pay_date: calc.pay_date,
          total_amount: calc.total_cost,
          total_boards: calc.total_units,
          total_locations: calc.location_count,
          service_type_name: calc.service_type_name || 'Initial Service Call',
          rate_type: calc.rate_type || 'FLAT',
          total_billable_hours: calc.billable_hours || 0,
          location_results: (locations || []).map(location => ({
            id: location.id,
            location_name: location.location_address,
            unit_count: location.units,
            boards: location.units, // Each unit corresponds to a board
            amount: location.total_cost,
            customer_name: location.customer_name || 'N/A',
            state: location.state || 'N/A',
            order_numbers: location.order_numbers || '',
            excluded_orders: location.excluded_orders || '',
            is_excluded: location.is_excluded || false,
            exclusion_reason: location.exclusion_reason || '',
            pay_date: calc.pay_date,
            service_type_name: location.service_type_name || calc.service_type_name || 'Initial Service Call',
            rate_type: location.rate_type || calc.rate_type || 'FLAT',
            travel_hours: Number(location.travel_hours) || 0,
            onsite_hours: Number(location.onsite_hours) || 0,
            billable_hours: Number(location.billable_hours) || 0,
            travel_time_start: location.travel_time_start,
            travel_time_end: location.travel_time_end,
            arrival_time: location.arrival_time,
            departure_time: location.departure_time
          })),
          work_week_from_date: calc.uploads?.work_week_from_date,
          work_week_to_date: calc.uploads?.work_week_to_date,
          has_hourly_work: calc.uploads?.has_hourly_work || hasHourlyLocations
        };
        
        providersWithLocations.push(formattedResult);
        
        // Add to grand totals
        grandTotalAmount += Number(calc.total_cost) || 0;
        grandTotalLocations += Number(calc.location_count) || 0;
        grandTotalBoards += Number(calc.total_units) || 0;
      }
      
      console.log('All providers with locations:', providersWithLocations);
      console.log('Total providers:', providersWithLocations.length);
      providersWithLocations.forEach((p, i) => {
        console.log(`Provider ${i}: ${p.provider_name} has ${p.location_results.length} locations`);
      });
      
      setAllResults(providersWithLocations);
      setTotalAmount(grandTotalAmount);
      setTotalLocations(grandTotalLocations);
      setTotalBoards(grandTotalBoards);
      
      // Determine if this is a refusal calculation to drive per-type revenue logic
      let isRefusal = false;
      if (calcResults.length > 0 && calcResults[0].uploads && calcResults[0].uploads.file_path) {
        isRefusal = calcResults[0].uploads.file_path.includes('/refusal/');
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        isRefusal = urlParams.get('refusal') === 'true';
      }
      setIsRefusalCalculation(isRefusal);
      setHasHourlyWork(hasHourlyWork);
      
      // Calculate service type summary
      const typeSummary = {};
      providersWithLocations.forEach(provider => {
        provider.location_results.forEach(location => {
          const serviceType = location.service_type_name;
          if (!typeSummary[serviceType]) {
            typeSummary[serviceType] = {
              locationCount: 0,
              unitCount: 0,
              totalCost: 0,
              revenue: 0,
              billableHours: 0,
              rateType: location.rate_type
            };
          }
          typeSummary[serviceType].locationCount += 1;
          typeSummary[serviceType].unitCount += location.unit_count;
          typeSummary[serviceType].totalCost += location.amount;
          typeSummary[serviceType].billableHours += location.billable_hours || 0;
        });
      });
      
      // Calculate revenue for each service type based on rules
      Object.keys(typeSummary).forEach(serviceType => {
        const summary = typeSummary[serviceType];
        if (summary.rateType === 'FLAT') {
          // For flat rate service types
          if (isRefusal) {
            summary.revenue = summary.unitCount * 50; // All flat types have $50 refusal rate
          } else {
            // Different completion rates based on service type
            if (serviceType === 'Initial Service Call') {
              summary.revenue = summary.locationCount * 300;
            } else if (serviceType === 'Ruffle Feathers') {
              summary.revenue = summary.locationCount * 150;
            } else {
              // If additional FLAT types exist with different rates, plug them here.
              // Fallback: treat as $0 to avoid overstating revenue for unknown types.
              summary.revenue = 0;
            }
          }
        } else if (summary.rateType === 'HOURLY') {
          // For hourly service types like Aramark Audit ($98/hour)
          summary.revenue = summary.billableHours * 98;
        }
      });
      
      // Overall totals should be the sum of service-type rows
      const totalServiceTypeRevenue = Object.values(typeSummary)
        .reduce((sum, s) => sum + (s.revenue || 0), 0);
      setCocaColaRevenue(totalServiceTypeRevenue);
      setNetProfit(totalServiceTypeRevenue - grandTotalAmount);
      
      setServiceTypeSummary(typeSummary);
      
      if (providersWithLocations.length > 0) {
        setActiveProvider(0); // Set first provider as active
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      setError('Failed to load calculation results: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/upload-data');
  };

  const handleProviderChange = (event, newValue) => {
    setActiveProvider(newValue);
  };

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportAllProviders = () => {
    handleExportMenuClose();
    exportAllProvidersCSV();
  };

  const handleExportEachProvider = () => {
    handleExportMenuClose();
    exportEachProviderCSV();
  };

  // escapeCSVField imported from bulkExportUtils.js

  const exportAllProvidersCSV = () => {
    if (allResults.length === 0) return;
    
    // Prepare CSV data for all providers
    const calculationType = isRefusalCalculation ? 'Refusal Work' : 'Completed Work';
    const headers = ['Provider', 'LogistixAI Order #', 'Customer Name', 'Location', 'State', 'Service Type', 'Rate Type', 'Unit Count'];
    
    // Add hourly columns if needed
    if (hasHourlyWork) {
      headers.push('Travel Hours', 'Onsite Hours', 'Billable Hours');
    }
    
    headers.push('Amount', 'Net Terms', 'Pay Date');
    
    let allRows = [];
    
    // Add calculation type as the first row
    allRows.push([`Calculation Type: ${calculationType}`, ...Array(headers.length - 1).fill('')]);
    allRows.push(Array(headers.length).fill('')); // Empty row for spacing
    
    // Add work week information to the header if available
    if (allResults.length > 0) {
      // Get work week dates from the first provider
      const fromDate = allResults[0].work_week_from_date ? 
        format(getAdjustedDate(allResults[0].work_week_from_date), 'MM/dd/yyyy') : 'Not set';
      
      const toDate = allResults[0].work_week_to_date ? 
        format(getAdjustedDate(allResults[0].work_week_to_date), 'MM/dd/yyyy') : 'Not set';
      
      allRows.push([`Work Week: ${fromDate} - ${toDate}`, ...Array(headers.length - 1).fill('')]);
      allRows.push(Array(headers.length).fill('')); // Empty row for spacing
    }
    
    // Add data for each provider
    allResults.forEach(provider => {
      const providerRows = provider.location_results.map(location => {
        const row = [
          provider.provider_name,
          location.order_numbers,
          location.customer_name,
          location.location_name,
          location.state,
          location.service_type_name,
          location.rate_type,
          location.unit_count
        ];
        
        if (hasHourlyWork) {
          row.push(
            location.rate_type === 'HOURLY' && location.travel_hours > 0 ? location.travel_hours.toFixed(2) : '',
            location.rate_type === 'HOURLY' && location.onsite_hours > 0 ? location.onsite_hours.toFixed(2) : '',
            location.rate_type === 'HOURLY' && location.billable_hours > 0 ? location.billable_hours.toFixed(2) : ''
          );
        }
        
        row.push(
          location.amount,
          provider.net_terms ? `Net ${provider.net_terms}` : '',
          provider.pay_date ? format(new Date(provider.pay_date), 'MM/dd/yyyy') : provider.net_terms ? 'Net Terms not available' : 'Pay Date not found'
        );
        
        return row;
      });
      
      allRows = [...allRows, ...providerRows];
    });
    
    // Add summary rows
    allRows.push(Array(headers.length).fill(''));
    
    // Add service type summary if multiple types
    if (Object.keys(serviceTypeSummary).length > 1) {
      allRows.push(['Service Type Summary', ...Array(headers.length - 1).fill('')]);
      Object.entries(serviceTypeSummary).forEach(([serviceType, summary]) => {
        const summaryRow = [serviceType, '', '', `${summary.locationCount} locations`, '', '', summary.rateType, summary.unitCount];
        if (hasHourlyWork) {
          summaryRow.push('', '', summary.billableHours > 0 ? summary.billableHours.toFixed(2) : '');
        }
        summaryRow.push(summary.totalCost, '', '');
        allRows.push(summaryRow);
      });
      allRows.push(Array(headers.length).fill(''));
    }
    
    // Add grand total
    const totalRow = ['Total', '', '', '', '', '', '', totalBoards];
    if (hasHourlyWork) {
      const totalHours = Object.values(serviceTypeSummary).reduce((sum, s) => sum + s.billableHours, 0);
      totalRow.push('', '', totalHours > 0 ? totalHours.toFixed(2) : '');
    }
    totalRow.push(totalAmount, '', '');
    allRows.push(totalRow);
    
    // Convert to CSV string with proper escaping for fields containing commas
    const csvContent = [
      headers.map(escapeCSVField).join(','),
      ...allRows.map(row => row.map(escapeCSVField).join(','))
    ].join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filePrefix = isRefusalCalculation ? 'refusal-work' : 'completed-work';
    link.setAttribute('download', `${filePrefix}-all-providers-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportEachProviderCSV = () => {
    if (allResults.length === 0) return;
    
    const calculationType = isRefusalCalculation ? 'Refusal Work' : 'Completed Work';
    const headers = ['LogistixAI Order #', 'Customer Name', 'Location', 'State', 'Service Type', 'Rate Type', 'Unit Count'];
    
    // Add hourly columns if needed
    if (hasHourlyWork) {
      headers.push('Travel Hours', 'Onsite Hours', 'Billable Hours');
    }
    
    headers.push('Amount', 'Pay Date');
    
    // Create and download a CSV for each provider
    allResults.forEach(provider => {
      // Start with calculation type rows
      const rows = [
        [`Calculation Type: ${calculationType}`, ...Array(headers.length - 1).fill('')],
        Array(headers.length).fill('') // Empty row for spacing
      ];
      
      // Add Net Terms information if available
      if (provider.net_terms) {
        rows.push([`Net Terms: Net ${provider.net_terms}`, ...Array(headers.length - 1).fill('')]);
      }
      
      // Add Pay Date information if available
      if (provider.pay_date) {
        rows.push([`Pay Date: ${format(new Date(provider.pay_date), 'MM/dd/yyyy')}`, ...Array(headers.length - 1).fill('')]);
      } else if (provider.net_terms) {
        rows.push(['Pay Date: Net Terms not available', ...Array(headers.length - 1).fill('')]);
      } else {
        rows.push(['Pay Date: Pay Date not found', ...Array(headers.length - 1).fill('')]);
      }
      
      // Add Work Week information
      const fromDate = provider.work_week_from_date ? 
        format(getAdjustedDate(provider.work_week_from_date), 'MM/dd/yyyy') : 'Not set';
      const toDate = provider.work_week_to_date ? 
        format(getAdjustedDate(provider.work_week_to_date), 'MM/dd/yyyy') : 'Not set';
      rows.push([`Work Week: ${fromDate} - ${toDate}`, ...Array(headers.length - 1).fill('')]);
      
      rows.push(Array(headers.length).fill('')); // Empty row for spacing
      
      // Add provider's location data
      provider.location_results.forEach(location => {
        const row = [
          location.order_numbers,
          location.customer_name,
          location.location_name,
          location.state,
          location.service_type_name,
          location.rate_type,
          location.unit_count.toString()
        ];
        
        if (hasHourlyWork) {
          row.push(
            location.rate_type === 'HOURLY' && location.travel_hours > 0 ? location.travel_hours.toFixed(2) : '',
            location.rate_type === 'HOURLY' && location.onsite_hours > 0 ? location.onsite_hours.toFixed(2) : '',
            location.rate_type === 'HOURLY' && location.billable_hours > 0 ? location.billable_hours.toFixed(2) : ''
          );
        }
        
        row.push(
          location.amount.toString(),
          provider.pay_date ? format(new Date(provider.pay_date), 'MM/dd/yyyy') : provider.net_terms ? 'Net Terms not available' : 'Pay Date not found'
        );
        
        rows.push(row);
      });
      
      // Add provider summary rows
      rows.push(Array(headers.length).fill(''));
      
      const totalRow = ['Total', '', '', '', '', '', provider.total_boards];
      if (hasHourlyWork) {
        const providerHours = provider.location_results
          .filter(loc => loc.rate_type === 'HOURLY')
          .reduce((sum, loc) => sum + loc.billable_hours, 0);
        totalRow.push('', '', providerHours > 0 ? providerHours.toFixed(2) : '');
      }
      totalRow.push(provider.total_amount, provider.pay_date ? format(new Date(provider.pay_date), 'MM/dd/yyyy') : provider.net_terms ? 'Net Terms not available' : 'Pay Date not found');
      rows.push(totalRow);
      
      // Convert to CSV string with proper escaping for fields containing commas
      const csvContent = [
        headers.map(escapeCSVField).join(','),
        ...rows.map(row => row.map(escapeCSVField).join(','))
      ].join('\n');
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Create a safe filename by replacing any invalid characters
      const safeProviderName = provider.provider_name.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
      const filePrefix = isRefusalCalculation ? 'refusal-work' : 'completed-work';
      link.setAttribute('download', `${filePrefix}-${safeProviderName}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url); // Clean up to avoid memory leaks
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // PDF Menu handlers
  const handlePdfMenuOpen = (event) => {
    setPdfMenuAnchor(event.currentTarget);
  };

  const handlePdfMenuClose = () => {
    setPdfMenuAnchor(null);
  };

  const handleExportAllProvidersPdf = () => {
    handlePdfMenuClose();
    exportAllProvidersPdf().catch(error => {
      console.error('Error generating PDF:', error);
      // You could add a snackbar or alert here to notify the user
    });
  };

  const handleExportEachProviderPdf = () => {
    handlePdfMenuClose();
    exportEachProviderPdf().catch(error => {
      console.error('Error generating PDFs:', error);
      // You could add a snackbar or alert here to notify the user
    });
  };

  // PDF Export Functions
  const exportAllProvidersPdf = async () => {
    if (allResults.length === 0) return;
    
    const calculationType = isRefusalCalculation ? 'Refusal Work' : 'Completed Work';
    const doc = await initializePDF('Statement of Work', `${calculationType} - All Providers`);
    
    // Add summary section
    let currentY = 180; // Start after the header section
    currentY = addSectionHeader(doc, 'Summary', currentY);
    
    const summaryData = [
      ['Calculation Type', calculationType],
      ['Total Amount', formatCurrency(totalAmount)],
      ['Total Locations', totalLocations.toString()],
      ['Total Units', totalBoards.toString()]
    ];
    
    // Add Work Week to summary
    if (allResults.length > 0) {
      const fromDate = allResults[0].work_week_from_date ? 
        format(getAdjustedDate(allResults[0].work_week_from_date), 'MM/dd/yyyy') : 'Not set';
      
      const toDate = allResults[0].work_week_to_date ? 
        format(getAdjustedDate(allResults[0].work_week_to_date), 'MM/dd/yyyy') : 'Not set';
      
      summaryData.push(['Work Week', `${fromDate} - ${toDate}`]);
    }
    
    autoTable(doc, {
      ...getTableStyles(),
      startY: currentY + 5,
      head: [['Metric', 'Value']],
      body: summaryData,
      columnStyles: {
        0: { cellWidth: 150 },
        1: { cellWidth: 150 }
      },
      tableWidth: 300
    });
    
    currentY = doc.lastAutoTable.finalY + 40;
    
    // Add Service Type Summary if multiple types exist
    if (Object.keys(serviceTypeSummary).length > 1) {
      currentY = addSectionHeader(doc, 'Summary by Service Type', currentY);
      
      const serviceTypeHeaders = ['Service Type', 'Type', 'Locations', 'Units'];
      if (hasHourlyWork) {
        serviceTypeHeaders.push('Hours');
      }
      serviceTypeHeaders.push('Provider Cost');
      
      const serviceTypeData = Object.entries(serviceTypeSummary).map(([serviceType, summary]) => {
        const row = [
          serviceType,
          summary.rateType,
          summary.locationCount.toString(),
          summary.unitCount.toString()
        ];
        
        if (hasHourlyWork) {
          row.push(summary.billableHours > 0 ? summary.billableHours.toFixed(2) : '-');
        }
        
        row.push(
          formatCurrency(summary.totalCost)
        );
        
        return row;
      });
      
      // Add total row
      const totalRow = [
        { content: 'TOTAL', styles: { fontStyle: 'bold' } },
        '-',
        totalLocations.toString(),
        totalBoards.toString()
      ];
      
      if (hasHourlyWork) {
        const totalHours = Object.values(serviceTypeSummary).reduce((sum, s) => sum + s.billableHours, 0);
        totalRow.push(totalHours > 0 ? totalHours.toFixed(2) : '-');
      }
      
      totalRow.push(
        formatCurrency(totalAmount)
      );
      
      serviceTypeData.push(totalRow);
      
      autoTable(doc, {
        ...getTableStyles(),
        startY: currentY + 5,
        head: [serviceTypeHeaders],
        body: serviceTypeData,
        columnStyles: hasHourlyWork ? {
          0: { cellWidth: 100 },
          1: { cellWidth: 40 },
          2: { cellWidth: 50 },
          3: { cellWidth: 40 },
          4: { cellWidth: 40 },
          5: { cellWidth: 70 },
          6: { cellWidth: 70 },
          7: { cellWidth: 60 }
        } : {
          0: { cellWidth: 120 },
          1: { cellWidth: 50 },
          2: { cellWidth: 60 },
          3: { cellWidth: 50 },
          4: { cellWidth: 80 },
          5: { cellWidth: 80 },
          6: { cellWidth: 70 }
        }
      });
      
      currentY = doc.lastAutoTable.finalY + 40;
    }
    
    // Add data for each provider
    allResults.forEach((provider, index) => {
      // Check if we need a new page
      if (currentY > doc.internal.pageSize.height - 150) {
        doc.addPage();
        currentY = 50;
      }
      
      currentY = addSectionHeader(doc, `Provider: ${provider.provider_name}`, currentY);

      // Add Net Terms if available
      if (provider.net_terms) {
        doc.setFontSize(11);
        doc.text(`Net Terms: Net ${provider.net_terms}`, 14, currentY);
        currentY += 15;
      }
      
      // Add Pay Date if available
      if (provider.pay_date) {
        doc.setFontSize(11);
        doc.text(`Pay Date: ${format(new Date(provider.pay_date), 'MM/dd/yyyy')}`, 14, currentY);
        currentY += 15;
      } else if (provider.net_terms) {
        doc.setFontSize(11);
        doc.text('Pay Date: Net Terms not available', 14, currentY);
        currentY += 15;
      } else {
        doc.setFontSize(11);
        doc.text('Pay Date: Pay Date not found', 14, currentY);
        currentY += 15;
      }
      
      // Group locations by service type
      const locationsByServiceType = {};
      provider.location_results.forEach(location => {
        const serviceType = location.service_type_name || 'Initial Service Call';
        if (!locationsByServiceType[serviceType]) {
          locationsByServiceType[serviceType] = {
            locations: [],
            rateType: location.rate_type || 'FLAT',
            subtotal: 0,
            unitCount: 0,
            totalHours: 0
          };
        }
        locationsByServiceType[serviceType].locations.push(location);
        locationsByServiceType[serviceType].subtotal += location.amount;
        locationsByServiceType[serviceType].unitCount += location.unit_count;
        locationsByServiceType[serviceType].totalHours += location.billable_hours || 0;
      });
      
      // Create tables for each service type
      Object.entries(locationsByServiceType).forEach(([serviceType, data]) => {
        // Add service type header
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`${serviceType} (${data.rateType})`, 14, currentY + 10);
        doc.setFont(undefined, 'normal');
        currentY += 20;
        
        if (data.rateType === 'HOURLY') {
          // Hourly work table
          const hourlyHeaders = ['Order #', 'Customer', 'Location', 'Travel Hrs', 'Onsite Hrs', 'Total Hrs', 'Amount'];
          const hourlyData = data.locations.map(location => {
            let orderDisplay = location.order_numbers ? location.order_numbers.split(' ').join(', ') : '-';
            // Show exclusion reason for excluded orders
            if (location.is_excluded && location.exclusion_reason) {
              orderDisplay += `\n[NON-PAYMENT: ${location.exclusion_reason}]`;
            } else if (location.excluded_orders && location.excluded_orders.trim()) {
              orderDisplay += `\n[Excluded: ${location.excluded_orders.split(' ').join(', ')}]`;
            }
            
            // Style for excluded rows
            const isExcluded = location.is_excluded;
            const rowStyle = isExcluded ? { textColor: [128, 128, 128], fontStyle: 'italic' } : {};
            
            return [
              { content: orderDisplay, styles: rowStyle },
              { content: location.customer_name, styles: rowStyle },
              { content: location.location_name, styles: rowStyle },
              { content: location.travel_hours > 0 ? location.travel_hours.toFixed(2) : '-', styles: rowStyle },
              { content: location.onsite_hours > 0 ? location.onsite_hours.toFixed(2) : '-', styles: rowStyle },
              { content: location.billable_hours > 0 ? location.billable_hours.toFixed(2) : '-', styles: rowStyle },
              { content: isExcluded ? '$0.00' : formatCurrency(location.amount), styles: isExcluded ? { ...rowStyle, textColor: [200, 0, 0] } : {} }
            ];
          });
          
          // Add subtotal row
          hourlyData.push([
            { content: 'SUBTOTAL', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: data.totalHours.toFixed(2), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: formatCurrency(data.subtotal), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
          ]);
          
          autoTable(doc, {
            ...getTableStyles(),
            startY: currentY,
            head: [hourlyHeaders],
            body: hourlyData,
            columnStyles: {
              0: { cellWidth: 70 },
              1: { cellWidth: 80 },
              2: { cellWidth: 120 },
              3: { cellWidth: 45 },
              4: { cellWidth: 45 },
              5: { cellWidth: 45 },
              6: { cellWidth: 60 }
            }
          });
        } else {
          // Flat rate work table
          const flatHeaders = ['Order #', 'Customer', 'Location', 'State', 'Units', 'Amount'];
          const flatData = data.locations.map(location => {
            let orderDisplay = location.order_numbers ? location.order_numbers.split(' ').join(', ') : '-';
            // Show exclusion reason for excluded orders
            if (location.is_excluded && location.exclusion_reason) {
              orderDisplay += `\n[NON-PAYMENT: ${location.exclusion_reason}]`;
            } else if (location.excluded_orders && location.excluded_orders.trim()) {
              orderDisplay += `\n[Excluded: ${location.excluded_orders.split(' ').join(', ')}]`;
            }
            
            // Style for excluded rows
            const isExcluded = location.is_excluded;
            const rowStyle = isExcluded ? { textColor: [128, 128, 128], fontStyle: 'italic' } : {};
            
            return [
              { content: orderDisplay, styles: rowStyle },
              { content: location.customer_name, styles: rowStyle },
              { content: location.location_name, styles: rowStyle },
              { content: location.state, styles: rowStyle },
              { content: location.unit_count.toString(), styles: rowStyle },
              { content: isExcluded ? '$0.00' : formatCurrency(location.amount), styles: isExcluded ? { ...rowStyle, textColor: [200, 0, 0] } : {} }
            ];
          });
          
          // Add subtotal row
          flatData.push([
            { content: 'SUBTOTAL', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: data.unitCount.toString(), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: formatCurrency(data.subtotal), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
          ]);
          
          autoTable(doc, {
            ...getTableStyles(),
            startY: currentY,
            head: [flatHeaders],
            body: flatData,
            columnStyles: {
              0: { cellWidth: 80 },
              1: { cellWidth: 100 },
              2: { cellWidth: 150 },
              3: { cellWidth: 60 },
              4: { cellWidth: 50 },
              5: { cellWidth: 80 }
            }
          });
        }
        
        currentY = doc.lastAutoTable.finalY + 20;
        
        // Check if we need a new page
        if (currentY > doc.internal.pageSize.height - 100) {
          doc.addPage();
          currentY = 50;
        }
      });
      
      // Add provider total
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Provider Total: ${formatCurrency(provider.total_amount)}`, 14, currentY);
      doc.setFont(undefined, 'normal');
      currentY += 30;
    });
    
    // Add footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      
      // Add page number
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 20,
        { align: 'center' }
      );
      
      // Add footer line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(
        PDF_STYLES_CONSTANTS.margins.left,
        doc.internal.pageSize.height - 30,
        doc.internal.pageSize.width - PDF_STYLES_CONSTANTS.margins.right,
        doc.internal.pageSize.height - 30
      );
    }
    
    // Save the PDF
    const filePrefix = isRefusalCalculation ? 'refusal-work' : 'completed-work';
    
    // Save the PDF with optimization options
    const pdfOptions = {
      compress: true,
      precision: 2,
      userUnit: 1.0,
      optimization: {
        noRasterizationsizeLimit: true
      }
    };
    
    doc.save(`${filePrefix}-all-providers-${new Date().toISOString().split('T')[0]}.pdf`, pdfOptions);
  };

  const exportEachProviderPdf = async () => {
    if (allResults.length === 0) return;
    
    // Create and download a PDF for each provider
    for (const provider of allResults) {
      const calculationType = isRefusalCalculation ? 'Refusal Work' : 'Completed Work';
      const doc = await initializePDF('Statement of Work', `${calculationType} - ${provider.provider_name}`);
      
      // Add provider summary
      let currentY = 180; // Start after the header section
      currentY = addSectionHeader(doc, 'Summary', currentY);
      
      const summaryData = [
        ['Calculation Type', calculationType],
        ['Total Amount', formatCurrency(provider.total_amount)],
        ['Total Locations', provider.total_locations.toString()],
        ['Total Units', provider.total_boards.toString()]
      ];
      
      // Add Net Terms to summary if available
      if (provider.net_terms) {
        summaryData.push(['Net Terms', `Net ${provider.net_terms}`]);
      }
      
      // Add Pay Date to summary if available
      if (provider.pay_date) {
        summaryData.push(['Pay Date', format(new Date(provider.pay_date), 'MM/dd/yyyy')]);
      } else if (provider.net_terms) {
        summaryData.push(['Pay Date', 'Net Terms not available']);
      } else {
        summaryData.push(['Pay Date', 'Pay Date not found']);
      }
      
      // Add Work Week to summary
      const fromDate = provider.work_week_from_date ? 
        format(getAdjustedDate(provider.work_week_from_date), 'MM/dd/yyyy') : 'Not set';
      const toDate = provider.work_week_to_date ? 
        format(getAdjustedDate(provider.work_week_to_date), 'MM/dd/yyyy') : 'Not set';
      summaryData.push(['Work Week', `${fromDate} - ${toDate}`]);
      
      autoTable(doc, {
        ...getTableStyles(),
        startY: currentY + 5,
        head: [['Metric', 'Value']],
        body: summaryData,
        columnStyles: {
          0: { cellWidth: 150 },
          1: { cellWidth: 150 }
        },
        tableWidth: 300
      });
      
      // Add location details
      currentY = doc.lastAutoTable.finalY + 40;
      currentY = addSectionHeader(doc, 'Location Details', currentY);
      
      // Group locations by service type
      const locationsByServiceType = {};
      provider.location_results.forEach(location => {
        const serviceType = location.service_type_name || 'Initial Service Call';
        if (!locationsByServiceType[serviceType]) {
          locationsByServiceType[serviceType] = {
            locations: [],
            rateType: location.rate_type || 'FLAT',
            subtotal: 0,
            unitCount: 0,
            totalHours: 0
          };
        }
        locationsByServiceType[serviceType].locations.push(location);
        locationsByServiceType[serviceType].subtotal += location.amount;
        locationsByServiceType[serviceType].unitCount += location.unit_count;
        locationsByServiceType[serviceType].totalHours += location.billable_hours || 0;
      });
      
      // Calculate if this provider has hourly work
      const providerHasHourlyWork = Object.values(locationsByServiceType).some(data => data.rateType === 'HOURLY');
      
      // Add service type breakdown if multiple types
      if (Object.keys(locationsByServiceType).length > 1) {
        currentY = addSectionHeader(doc, 'Service Type Summary', currentY);
        
        const serviceHeaders = ['Service Type', 'Type', 'Locations', 'Units'];
        if (providerHasHourlyWork) {
          serviceHeaders.push('Hours');
        }
        serviceHeaders.push('Amount');
        
        const serviceData = Object.entries(locationsByServiceType).map(([serviceType, data]) => {
          const row = [
            serviceType,
            data.rateType,
            data.locations.length.toString(),
            data.unitCount.toString()
          ];
          
          if (providerHasHourlyWork) {
            row.push(data.totalHours > 0 ? data.totalHours.toFixed(2) : '-');
          }
          
          row.push(formatCurrency(data.subtotal));
          return row;
        });
        
        // Add total row
        const totalRow = [
          { content: 'TOTAL', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
          { content: '', styles: { fillColor: [240, 240, 240] } },
          { content: provider.total_locations.toString(), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
          { content: provider.total_boards.toString(), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
        ];
        
        if (providerHasHourlyWork) {
          const totalHours = Object.values(locationsByServiceType).reduce((sum, data) => sum + data.totalHours, 0);
          totalRow.push({ content: totalHours > 0 ? totalHours.toFixed(2) : '-', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } });
        }
        
        totalRow.push({ content: formatCurrency(provider.total_amount), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } });
        serviceData.push(totalRow);
        
        autoTable(doc, {
          ...getTableStyles(),
          startY: currentY + 5,
          head: [serviceHeaders],
          body: serviceData,
          columnStyles: providerHasHourlyWork ? {
            0: { cellWidth: 140 },
            1: { cellWidth: 60 },
            2: { cellWidth: 60 },
            3: { cellWidth: 50 },
            4: { cellWidth: 50 },
            5: { cellWidth: 80 }
          } : {
            0: { cellWidth: 180 },
            1: { cellWidth: 60 },
            2: { cellWidth: 60 },
            3: { cellWidth: 60 },
            4: { cellWidth: 80 }
          }
        });
        
        currentY = doc.lastAutoTable.finalY + 40;
      }
      
      // Create tables for each service type
      Object.entries(locationsByServiceType).forEach(([serviceType, data]) => {
        // Check if we need a new page
        if (currentY > doc.internal.pageSize.height - 150) {
          doc.addPage();
          currentY = 50;
        }
        
        // Add service type header
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`${serviceType} (${data.rateType})`, 14, currentY);
        doc.setFont(undefined, 'normal');
        currentY += 10;
        
        if (data.rateType === 'HOURLY') {
          // Hourly work table
          const hourlyHeaders = ['Order #', 'Customer', 'Location', 'Travel Hrs', 'Onsite Hrs', 'Total Hrs', 'Amount'];
          const hourlyData = data.locations.map(location => {
            let orderDisplay = location.order_numbers ? location.order_numbers.split(' ').join(', ') : '-';
            // Show exclusion reason for excluded orders
            if (location.is_excluded && location.exclusion_reason) {
              orderDisplay += `\n[NON-PAYMENT: ${location.exclusion_reason}]`;
            } else if (location.excluded_orders && location.excluded_orders.trim()) {
              orderDisplay += `\n[Excluded: ${location.excluded_orders.split(' ').join(', ')}]`;
            }
            
            // Style for excluded rows
            const isExcluded = location.is_excluded;
            const rowStyle = isExcluded ? { textColor: [128, 128, 128], fontStyle: 'italic' } : {};
            
            return [
              { content: orderDisplay, styles: rowStyle },
              { content: location.customer_name, styles: rowStyle },
              { content: location.location_name, styles: rowStyle },
              { content: location.travel_hours > 0 ? location.travel_hours.toFixed(2) : '-', styles: rowStyle },
              { content: location.onsite_hours > 0 ? location.onsite_hours.toFixed(2) : '-', styles: rowStyle },
              { content: location.billable_hours > 0 ? location.billable_hours.toFixed(2) : '-', styles: rowStyle },
              { content: isExcluded ? '$0.00' : formatCurrency(location.amount), styles: isExcluded ? { ...rowStyle, textColor: [200, 0, 0] } : {} }
            ];
          });
          
          // Add subtotal row
          hourlyData.push([
            { content: 'SUBTOTAL', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: data.totalHours.toFixed(2), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: formatCurrency(data.subtotal), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
          ]);
          
          autoTable(doc, {
            ...getTableStyles(),
            startY: currentY,
            head: [hourlyHeaders],
            body: hourlyData,
            columnStyles: {
              0: { cellWidth: 70 },
              1: { cellWidth: 80 },
              2: { cellWidth: 120 },
              3: { cellWidth: 45 },
              4: { cellWidth: 45 },
              5: { cellWidth: 45 },
              6: { cellWidth: 60 }
            }
          });
        } else {
          // Flat rate work table
          const flatHeaders = ['Order #', 'Customer', 'Location', 'State', 'Units', 'Amount'];
          const flatData = data.locations.map(location => {
            let orderDisplay = location.order_numbers ? location.order_numbers.split(' ').join(', ') : '-';
            // Show exclusion reason for excluded orders
            if (location.is_excluded && location.exclusion_reason) {
              orderDisplay += `\n[NON-PAYMENT: ${location.exclusion_reason}]`;
            } else if (location.excluded_orders && location.excluded_orders.trim()) {
              orderDisplay += `\n[Excluded: ${location.excluded_orders.split(' ').join(', ')}]`;
            }
            
            // Style for excluded rows
            const isExcluded = location.is_excluded;
            const rowStyle = isExcluded ? { textColor: [128, 128, 128], fontStyle: 'italic' } : {};
            
            return [
              { content: orderDisplay, styles: rowStyle },
              { content: location.customer_name, styles: rowStyle },
              { content: location.location_name, styles: rowStyle },
              { content: location.state, styles: rowStyle },
              { content: location.unit_count.toString(), styles: rowStyle },
              { content: isExcluded ? '$0.00' : formatCurrency(location.amount), styles: isExcluded ? { ...rowStyle, textColor: [200, 0, 0] } : {} }
            ];
          });
          
          // Add subtotal row
          flatData.push([
            { content: 'SUBTOTAL', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: data.unitCount.toString(), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
            { content: formatCurrency(data.subtotal), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
          ]);
          
          autoTable(doc, {
            ...getTableStyles(),
            startY: currentY,
            head: [flatHeaders],
            body: flatData,
            columnStyles: {
              0: { cellWidth: 80 },
              1: { cellWidth: 100 },
              2: { cellWidth: 150 },
              3: { cellWidth: 60 },
              4: { cellWidth: 50 },
              5: { cellWidth: 80 }
            }
          });
        }
        
        currentY = doc.lastAutoTable.finalY + 20;
      });
      
      // Add grand total
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`GRAND TOTAL: ${formatCurrency(provider.total_amount)}`, 14, currentY);
      doc.setFont(undefined, 'normal');

      // Add footer with page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        
        // Add page number
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 20,
          { align: 'center' }
        );
        
        // Add footer line
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(
          PDF_STYLES_CONSTANTS.margins.left,
          doc.internal.pageSize.height - 30,
          doc.internal.pageSize.width - PDF_STYLES_CONSTANTS.margins.right,
          doc.internal.pageSize.height - 30
        );
      }
      
      // Create a safe filename by replacing any invalid characters
      const safeProviderName = provider.provider_name.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
      const filePrefix = isRefusalCalculation ? 'refusal-work' : 'completed-work';
      
      // Save the PDF with optimization options
      const pdfOptions = {
        compress: true,
        precision: 2,
        userUnit: 1.0,
        optimization: {
          noRasterizationsizeLimit: true
        }
      };
      
      doc.save(`${filePrefix}-${safeProviderName}-${new Date().toISOString().split('T')[0]}.pdf`, pdfOptions);
    }
  };

  // Archive Handlers
  const handleArchiveClick = (upload) => {
    setFileToArchive(upload);
    setArchiveDialogOpen(true);
  };

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
    setFileToArchive(null);
  };

  const handleArchiveConfirm = async () => {
    if (!fileToArchive) return;
    
    try {
      setLoading(true);
      
      // Update the record to be archived
      const { error: dbError } = await supabase
        .from('uploads')
        .update({ 
          is_archived: true,
          archived_at: new Date().toISOString()
        })
        .eq('id', fileToArchive.id);
        
      if (dbError) {
        throw dbError;
      }
      
      // Refresh both lists
      fetchRecentCalculations();
      fetchArchivedCalculations();
      setSnackbarMessage('Calculation archived successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error archiving upload:', error);
      setError('Failed to archive file: ' + error.message);
    } finally {
      setLoading(false);
      setArchiveDialogOpen(false);
      setFileToArchive(null);
    }
  };

  // Restore Handlers
  const handleRestoreClick = (upload) => {
    setFileToRestore(upload);
    setRestoreDialogOpen(true);
  };

  const handleRestoreCancel = () => {
    setRestoreDialogOpen(false);
    setFileToRestore(null);
  };

  const handleRestoreConfirm = async () => {
    if (!fileToRestore) return;
    
    try {
      setLoading(true);
      
      // Update the record to be un-archived
      const { error: dbError } = await supabase
        .from('uploads')
        .update({ 
          is_archived: false,
          archived_at: null
        })
        .eq('id', fileToRestore.id);
        
      if (dbError) {
        throw dbError;
      }
      
      // Refresh both lists
      fetchRecentCalculations();
      fetchArchivedCalculations();
      setSnackbarMessage('Calculation restored successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error restoring upload:', error);
      setError('Failed to restore file: ' + error.message);
    } finally {
      setLoading(false);
      setRestoreDialogOpen(false);
      setFileToRestore(null);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleDownload = async (upload) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(upload.file_path);
        
      if (error) {
        throw error;
      }
      
      // Create a download link and trigger the download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = upload.filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file: ' + error.message);
    }
  };
  
  // Modify the renderRecentCalculations function to use SortableTable
  const renderRecentCalculations = () => {
    const isArchiveTab = activeTab === 1;
    const currentRows = isArchiveTab ? archivedCalculations : recentCalculations;

    // Column definitions for recent calculations table
    const headCells = [
      { id: 'filename', label: 'Filename', align: 'left' },
      { id: 'created_at', label: isArchiveTab ? 'Archived Date' : 'Date', align: 'left' },
      { id: 'type', label: 'Type', align: 'left' },
      { id: 'work_week', label: 'Work Week', align: 'left' },
      { id: 'total_amount', label: 'Total Amount', align: 'left' },
      { id: 'uploaded_by', label: 'Uploaded By', align: 'left' },
      { id: 'actions', label: 'Actions', align: 'right', disableSorting: true },
    ];

    // Render function for each row
    const renderRow = (calc) => (
      <TableRow key={calc.id}>
        <TableCell>{calc.filename}</TableCell>
        <TableCell>{format(new Date(isArchiveTab ? (calc.archived_at || calc.created_at) : calc.created_at), 'MM/dd/yyyy h:mm a')}</TableCell>
        <TableCell>
          <Chip 
            label={calc.file_path.includes('/refusal/') ? "Refusal Work" : "Completed Work"} 
            color={calc.file_path.includes('/refusal/') ? "secondary" : "primary"}
            size="small"
          />
        </TableCell>
        <TableCell>
          {(calc.work_week_from_date && calc.work_week_to_date) ? (
            `${format(getAdjustedDate(calc.work_week_from_date), 'MM/dd/yyyy')} - ${format(getAdjustedDate(calc.work_week_to_date), 'MM/dd/yyyy')}`
          ) : (
            'Not set'
          )}
        </TableCell>
        <TableCell>
          {calc.total_amount ? `$${parseFloat(calc.total_amount).toFixed(2)}` : 'N/A'}
        </TableCell>
        <TableCell>{calc.uploaded_by || 'Unknown'}</TableCell>
        <TableCell align="right">
          <Tooltip title="View Results">
            <IconButton 
              onClick={() => navigate(`/results/${calc.id}?refusal=${calc.file_path.includes('/refusal/')}`)}
              color="primary"
              size="small"
              sx={{ mr: 1 }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download File">
            <IconButton 
              onClick={() => handleDownload(calc)}
              size="small"
              sx={{ mr: 1 }}
            >
              <GetAppIcon />
            </IconButton>
          </Tooltip>
          
          {isArchiveTab ? (
            <Tooltip title="Restore">
              <IconButton 
                onClick={() => handleRestoreClick(calc)}
                color="success"
                size="small"
              >
                <UnarchiveIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Archive">
              <IconButton 
                onClick={() => handleArchiveClick(calc)}
                color="warning"
                size="small"
              >
                <ArchiveIcon />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    );

    return (
      <Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="recent calculations tabs">
            <Tab label="Recent Calculations" id="tab-recent" aria-controls="tabpanel-recent" />
            <Tab label={`Archived (${archivedCalculations.length})`} id="tab-archive" aria-controls="tabpanel-archive" />
          </Tabs>
        </Box>

        <div role="tabpanel" hidden={activeTab !== 0} id="tabpanel-recent" aria-labelledby="tab-recent">
          {activeTab === 0 && (
            recentCalculations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Alert severity="info" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                  No recent calculations found.
                </Alert>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/upload-data')}
                  sx={{ mt: 2 }}
                >
                  Go to Upload Page
                </Button>
              </Box>
            ) : (
              <SortableTable
                rows={recentCalculations}
                headCells={headCells}
                defaultOrderBy="created_at"
                defaultOrder="desc"
                tableId="recent-calculations-table"
                defaultRowsPerPage={25}
                renderRow={renderRow}
                emptyContent={
                  <Alert severity="info">No recent calculations found.</Alert>
                }
              />
            )
          )}
        </div>

        <div role="tabpanel" hidden={activeTab !== 1} id="tabpanel-archive" aria-labelledby="tab-archive">
          {activeTab === 1 && (
            archivedCalculations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Alert severity="info" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                  No archived calculations found.
                </Alert>
              </Box>
            ) : (
              <SortableTable
                rows={archivedCalculations}
                headCells={headCells}
                defaultOrderBy="archived_at"
                defaultOrder="desc"
                tableId="archived-calculations-table"
                defaultRowsPerPage={25}
                renderRow={renderRow}
                emptyContent={
                  <Alert severity="info">No archived calculations found.</Alert>
                }
              />
            )
          )}
        </div>
      </Box>
    );
  };

  // Add function to handle opening the email dialog
  const handleEmailDialogOpen = () => {
    // Initialize selected providers (only those with emails selected by default)
    const initialSelection = {};
    allResults.forEach(provider => {
      initialSelection[provider.provider_id] = !!provider.provider_email;
    });
    setSelectedProviders(initialSelection);
    setEmailDialogOpen(true);
  };

  // Add function to handle closing the email dialog
  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setEmailResults(null);
  };

  // Add function to handle provider selection change
  const handleProviderSelectionChange = (providerId) => {
    setSelectedProviders(prev => ({
      ...prev,
      [providerId]: !prev[providerId]
    }));
  };

  // Add function to handle select/deselect all
  const handleSelectAllProviders = (selectAll) => {
    const newSelection = {};
    allResults.forEach(provider => {
      // Only allow selection of providers with email addresses
      if (provider.provider_email) {
        newSelection[provider.provider_id] = selectAll;
      } else {
        newSelection[provider.provider_id] = false;
      }
    });
    setSelectedProviders(newSelection);
  };

  // Component to display provider emails in the dialog
  const ProviderEmailListItem = ({ provider }) => {
    const [providerEmails, setProviderEmails] = useState([]);
    const [emailsLoading, setEmailsLoading] = useState(true);
    
    useEffect(() => {
      const loadEmails = async () => {
        let emails = await fetchProviderEmails(provider.provider_id);
        
        // If no emails in new system, show legacy email
        if (emails.length === 0 && provider.provider_email) {
          emails = [{
            email: provider.provider_email,
            email_type: 'legacy',
            is_primary: true,
            is_active: true
          }];
        }
        
        setProviderEmails(emails);
        setEmailsLoading(false);
      };
      
      loadEmails();
    }, [provider.provider_id, provider.provider_email]);
    
    const hasEmails = providerEmails.length > 0;
    
    return (
      <ListItem key={provider.provider_id} divider>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!selectedProviders[provider.provider_id]}
              onChange={() => handleProviderSelectionChange(provider.provider_id)}
              disabled={sendingEmails || !hasEmails}
            />
          }
          label=""
        />
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1">{provider.provider_name}</Typography>
              {!hasEmails && (
                <Typography 
                  component="span" 
                  variant="caption" 
                  sx={{ 
                    ml: 1,
                    color: 'error.main',
                    border: '1px solid',
                    borderColor: 'error.main',
                    borderRadius: 1,
                    px: 0.5
                  }}
                >
                  No Email
                </Typography>
              )}
            </Box>
          }
          secondary={
            emailsLoading ? (
              <CircularProgress size={16} />
            ) : (
              <>
                {hasEmails ? (
                  <Box>
                    <Typography component="span" variant="body2" color="text.secondary">
                      {providerEmails.length} email address{providerEmails.length !== 1 ? 'es' : ''}:
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {providerEmails.map((email, index) => (
                        <Chip
                          key={index}
                          label={
                            email.email_type === 'legacy' 
                              ? `${email.email} (legacy)` 
                              : `${email.email} (${email.email_type})`
                          }
                          size="small"
                          color={email.is_primary ? 'primary' : 'default'}
                          variant={email.is_primary ? 'filled' : 'outlined'}
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography component="span" variant="body2" color="error.main">
                    No email addresses configured. Add emails in the Provider Rate Table.
                  </Typography>
                )}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                  Amount: {formatCurrency(provider.total_amount)}
                </Typography>
              </>
            )
          }
        />
      </ListItem>
    );
  };

  // Function to get work week dates formatted for email
  const getWorkWeekDateRange = () => {
    if (allResults.length > 0 && allResults[0].work_week_from_date && allResults[0].work_week_to_date) {
      const fromDate = format(getAdjustedDate(allResults[0].work_week_from_date), 'MM/dd/yyyy');
      const toDate = format(getAdjustedDate(allResults[0].work_week_to_date), 'MM/dd/yyyy');
      return `${fromDate}-${toDate}`;
    }
    return 'N/A';
  };

  // Function to fetch provider emails from the new provider_emails table
  const fetchProviderEmails = async (providerId) => {
    try {
      // Get auth token
      const { data: authData } = await supabase.auth.getSession();
      const token = authData?.session?.access_token;

      if (!token) {
        console.error('No authentication token available');
        return [];
      }
      
      const response = await fetch(`/api/provider-emails/${providerId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        return data.emails.filter(email => email.is_active);
      }
      return [];
    } catch (error) {
      console.error('Error fetching provider emails:', error);
      return [];
    }
  };

  // Function to send emails with PDF attachments
  const handleSendEmails = async () => {
    try {
      setSendingEmails(true);
      
      // Get selected providers
      const selectedProviderIds = Object.keys(selectedProviders).filter(id => selectedProviders[id]);
      if (selectedProviderIds.length === 0) {
        setSnackbarMessage('Please select at least one provider to email');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const selectedProviderList = allResults.filter(provider => 
        selectedProviders[provider.provider_id]
      );

      // Fetch email addresses for all selected providers
      const providerEmailsMap = {};
      let totalEmailCount = 0;
      
      for (const provider of selectedProviderList) {
        let emails = await fetchProviderEmails(provider.provider_id);
        
        // If no emails in new system, fall back to legacy email
        if (emails.length === 0 && provider.provider_email) {
          emails = [{
            email: provider.provider_email,
            email_type: 'legacy',
            is_primary: true,
            is_active: true
          }];
        }
        
        providerEmailsMap[provider.provider_id] = emails;
        totalEmailCount += emails.length;
      }

      // Check for providers without email addresses
      const providersWithoutEmail = selectedProviderList.filter(provider => 
        !providerEmailsMap[provider.provider_id] || providerEmailsMap[provider.provider_id].length === 0
      );
      
      if (providersWithoutEmail.length > 0) {
        const providerNames = providersWithoutEmail.map(p => p.provider_name).join(', ');
        setSnackbarMessage(`Cannot send emails: Missing email addresses for: ${providerNames}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setSendingEmails(false);
        return;
      }

      // Show confirmation for multiple emails
      if (totalEmailCount > selectedProviderList.length) {
        const confirmed = window.confirm(
          `This will send ${totalEmailCount} emails to ${selectedProviderList.length} providers. Some providers have multiple email addresses. Continue?`
        );
        if (!confirmed) {
          setSendingEmails(false);
          return;
        }
      }

      // Generate email batches - one email per recipient
      const emailBatch = [];
      const workWeekRange = getWorkWeekDateRange();
      const today = format(new Date(), 'MM/dd/yyyy');
      
      for (const provider of selectedProviderList) {
        const providerEmails = providerEmailsMap[provider.provider_id];
        
        // Generate PDF once per provider
        const doc = await generateProviderPDF(provider);
        const pdfBase64 = doc.output('datauristring').split(',')[1];
        
        // Calculate service type summary for this provider
        const providerServiceTypes = {};
        let hasMultipleTypes = false;
        let totalHourlyHours = 0;
        
        provider.location_results.forEach(location => {
          const serviceType = location.service_type_name || 'Initial Service Call';
          const rateType = location.rate_type || 'FLAT';
          
          if (!providerServiceTypes[serviceType]) {
            providerServiceTypes[serviceType] = {
              count: 0,
              amount: 0,
              rateType: rateType,
              hours: 0
            };
          }
          
          providerServiceTypes[serviceType].count++;
          providerServiceTypes[serviceType].amount += location.amount;
          if (rateType === 'HOURLY') {
            providerServiceTypes[serviceType].hours += location.billable_hours || 0;
            totalHourlyHours += location.billable_hours || 0;
          }
        });
        
        hasMultipleTypes = Object.keys(providerServiceTypes).length > 1;
        
        // Build service type summary text for email
        let serviceTypeSummary = '';
        if (hasMultipleTypes) {
          serviceTypeSummary = Object.entries(providerServiceTypes)
            .map(([type, data]) => {
              if (data.rateType === 'HOURLY') {
                return `${type}: ${data.count} locations, ${data.hours.toFixed(2)} hours, ${formatCurrency(data.amount)}`;
              } else {
                return `${type}: ${data.count} locations, ${formatCurrency(data.amount)}`;
              }
            })
            .join('\n');
        }
        
        // Create email entry for each email address
        for (const emailRecord of providerEmails) {
          emailBatch.push({
            to: emailRecord.email,
            vendorName: provider.provider_name,
            vendorId: provider.provider_id,
            periodStart: workWeekRange.split('-')[0],
            periodEnd: workWeekRange.split('-')[1],
            documentDate: today,
            paymentAmount: formatCurrency(provider.total_amount),
            net_terms: provider.net_terms ? `Net ${provider.net_terms}` : '',
            pay_date: provider.pay_date ? format(new Date(provider.pay_date), 'MM/dd/yyyy') : '',
            pdfContent: pdfBase64,
            pdfFileName: `${isRefusalCalculation ? 'refusal' : 'completed'}-work-${provider.provider_name.replace(/\s+/g, '-').toLowerCase()}.pdf`,
            isRefusal: isRefusalCalculation,
            emailType: emailRecord.email_type,
            isPrimary: emailRecord.is_primary,
            // Add service type information
            hasMultipleServiceTypes: hasMultipleTypes,
            serviceTypeSummary: serviceTypeSummary,
            totalLocations: provider.total_locations,
            totalUnits: provider.total_boards,
            hasHourlyWork: totalHourlyHours > 0,
            totalHourlyHours: totalHourlyHours > 0 ? totalHourlyHours.toFixed(2) : '0'
          });
        }
      }

      if (emailBatch.length === 0) {
        setSnackbarMessage('No emails to send: No providers with valid email addresses were selected');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        setSendingEmails(false);
        return;
      }

      // Split email batch into smaller chunks to prevent payload size issues
      const BATCH_SIZE = 3;
      const emailBatches = [];
      for (let i = 0; i < emailBatch.length; i += BATCH_SIZE) {
        emailBatches.push(emailBatch.slice(i, i + BATCH_SIZE));
      }

      // Process each batch and collect results
      const emailResults = [];
      let hasAnyErrors = false;

      // Send batches sequentially to avoid overwhelming the server
      for (let i = 0; i < emailBatches.length; i++) {
        const batch = emailBatches[i];
        const batchNumber = i + 1;
        
        try {
          const isDevelopment = process.env.NODE_ENV === 'development';
          const apiUrl = isDevelopment 
            ? 'http://localhost:5000/api/email/send-batch' 
            : '/api/email/send-batch';

          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(batch),
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`API Error Response Text (Batch ${batchNumber}/${emailBatches.length}):`, errorText);
            throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
          }

          const result = await response.json();
          emailResults.push(...(result.results || []));
          
          if (!result.success) {
            hasAnyErrors = true;
          }
        } catch (error) {
          console.error(`Error sending batch ${batchNumber}/${emailBatches.length}:`, error);
          
          const failedResults = batch.map(email => ({
            to: email.to,
            success: false,
            message: `Failed to send: ${error.message}`,
            emailType: email.emailType,
            isPrimary: email.isPrimary
          }));
          
          emailResults.push(...failedResults);
          hasAnyErrors = true;
        }
      }

      // Set the combined results
      setEmailResults({ 
        success: !hasAnyErrors,
        results: emailResults
      });
      
      // Show success/warning message
      if (!hasAnyErrors) {
        setSnackbarMessage(`Successfully sent ${emailResults.length} emails to ${selectedProviderList.length} providers!`);
        setSnackbarSeverity('success');
      } else {
        const failedCount = emailResults.filter(r => !r.success).length;
        setSnackbarMessage(`${failedCount} out of ${emailResults.length} emails failed to send.`);
        setSnackbarSeverity('warning');
      }
      
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error sending emails:', error);
      setSnackbarMessage(`Failed to send emails: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSendingEmails(false);
    }
  };

  // Function to generate a single provider PDF
  const generateProviderPDF = async (provider) => {
    const calculationType = isRefusalCalculation ? 'Refusal Work' : 'Completed Work';
    const doc = await initializePDF('Statement of Work', `${calculationType} - ${provider.provider_name}`);
    
    // Add provider summary
    let currentY = 180; // Start after the header section
    currentY = addSectionHeader(doc, 'Summary', currentY);
    
    const summaryData = [
      ['Calculation Type', calculationType],
      ['Total Amount', formatCurrency(provider.total_amount)],
      ['Total Locations', provider.total_locations.toString()],
      ['Total Units', provider.total_boards.toString()]
    ];
    
    // Add Net Terms to summary if available
    if (provider.net_terms) {
      summaryData.push(['Net Terms', `Net ${provider.net_terms}`]);
    }
    
    // Add Pay Date to summary if available
    if (provider.pay_date) {
      summaryData.push(['Pay Date', format(new Date(provider.pay_date), 'MM/dd/yyyy')]);
    }
    
    // Add Work Week to summary
    if (provider.work_week_from_date && provider.work_week_to_date) {
      const fromDate = format(getAdjustedDate(provider.work_week_from_date), 'MM/dd/yyyy');
      const toDate = format(getAdjustedDate(provider.work_week_to_date), 'MM/dd/yyyy');
      summaryData.push(['Work Week', `${fromDate} - ${toDate}`]);
    }
    
    autoTable(doc, {
      ...getTableStyles(),
      startY: currentY + 5,
      head: [['Metric', 'Value']],
      body: summaryData,
      columnStyles: {
        0: { cellWidth: 150 },
        1: { cellWidth: 150 }
      },
      tableWidth: 300
    });
    
    currentY = doc.lastAutoTable.finalY + 40;
    
    // Group locations by service type
    const locationsByServiceType = {};
    provider.location_results.forEach(location => {
      const serviceType = location.service_type_name || 'Initial Service Call';
      if (!locationsByServiceType[serviceType]) {
        locationsByServiceType[serviceType] = {
          locations: [],
          rateType: location.rate_type || 'FLAT',
          subtotal: 0,
          unitCount: 0,
          totalHours: 0
        };
      }
      locationsByServiceType[serviceType].locations.push(location);
      locationsByServiceType[serviceType].subtotal += location.amount;
      locationsByServiceType[serviceType].unitCount += location.unit_count;
      locationsByServiceType[serviceType].totalHours += location.billable_hours || 0;
    });
    
    // Calculate if this provider has hourly work
    const providerHasHourlyWork = Object.values(locationsByServiceType).some(data => data.rateType === 'HOURLY');
    
    // Add service type breakdown if multiple types
    if (Object.keys(locationsByServiceType).length > 1) {
      currentY = addSectionHeader(doc, 'Service Type Summary', currentY);
      
      const serviceHeaders = ['Service Type', 'Type', 'Locations', 'Units'];
      if (providerHasHourlyWork) {
        serviceHeaders.push('Hours');
      }
      serviceHeaders.push('Amount');
      
      const serviceData = Object.entries(locationsByServiceType).map(([serviceType, data]) => {
        const row = [
          serviceType,
          data.rateType,
          data.locations.length.toString(),
          data.unitCount.toString()
        ];
        
        if (providerHasHourlyWork) {
          row.push(data.totalHours > 0 ? data.totalHours.toFixed(2) : '-');
        }
        
        row.push(formatCurrency(data.subtotal));
        return row;
      });
      
      // Add total row
      const totalRow = [
        { content: 'TOTAL', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
        { content: '', styles: { fillColor: [240, 240, 240] } },
        { content: provider.total_locations.toString(), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
        { content: provider.total_boards.toString(), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
      ];
      
      if (providerHasHourlyWork) {
        const totalHours = Object.values(locationsByServiceType).reduce((sum, data) => sum + data.totalHours, 0);
        totalRow.push({ content: totalHours > 0 ? totalHours.toFixed(2) : '-', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } });
      }
      
      totalRow.push({ content: formatCurrency(provider.total_amount), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } });
      serviceData.push(totalRow);
      
      autoTable(doc, {
        ...getTableStyles(),
        startY: currentY + 5,
        head: [serviceHeaders],
        body: serviceData,
        columnStyles: providerHasHourlyWork ? {
          0: { cellWidth: 140 },
          1: { cellWidth: 60 },
          2: { cellWidth: 60 },
          3: { cellWidth: 50 },
          4: { cellWidth: 50 },
          5: { cellWidth: 80 }
        } : {
          0: { cellWidth: 180 },
          1: { cellWidth: 60 },
          2: { cellWidth: 60 },
          3: { cellWidth: 60 },
          4: { cellWidth: 80 }
        }
      });
      
      currentY = doc.lastAutoTable.finalY + 40;
    }
    
    currentY = addSectionHeader(doc, 'Location Details', currentY);
    
    // Create tables for each service type
    Object.entries(locationsByServiceType).forEach(([serviceType, data]) => {
      // Check if we need a new page
      if (currentY > doc.internal.pageSize.height - 150) {
        doc.addPage();
        currentY = 50;
      }
      
      // Add service type header
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`${serviceType} (${data.rateType})`, 14, currentY);
      doc.setFont(undefined, 'normal');
      currentY += 10;
      
      if (data.rateType === 'HOURLY') {
        // Hourly work table
        const hourlyHeaders = ['Order #', 'Customer', 'Location', 'Travel Hrs', 'Onsite Hrs', 'Total Hrs', 'Amount'];
        const hourlyData = data.locations.map(location => [
          location.order_numbers.split(' ').join(', '),
          location.customer_name,
          location.location_name,
          location.travel_hours > 0 ? location.travel_hours.toFixed(2) : '-',
          location.onsite_hours > 0 ? location.onsite_hours.toFixed(2) : '-',
          location.billable_hours > 0 ? location.billable_hours.toFixed(2) : '-',
          formatCurrency(location.amount)
        ]);
        
        // Add subtotal row
        hourlyData.push([
          { content: 'SUBTOTAL', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
          { content: '', styles: { fillColor: [240, 240, 240] } },
          { content: '', styles: { fillColor: [240, 240, 240] } },
          { content: '', styles: { fillColor: [240, 240, 240] } },
          { content: '', styles: { fillColor: [240, 240, 240] } },
          { content: data.totalHours.toFixed(2), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
          { content: formatCurrency(data.subtotal), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
        ]);
        
        autoTable(doc, {
          ...getTableStyles(),
          startY: currentY,
          head: [hourlyHeaders],
          body: hourlyData,
          columnStyles: {
            0: { cellWidth: 70 },
            1: { cellWidth: 80 },
            2: { cellWidth: 120 },
            3: { cellWidth: 45 },
            4: { cellWidth: 45 },
            5: { cellWidth: 45 },
            6: { cellWidth: 60 }
          }
        });
      } else {
        // Flat rate work table
        const flatHeaders = ['Order #', 'Customer', 'Location', 'State', 'Units', 'Amount'];
        const flatData = data.locations.map(location => [
          location.order_numbers.split(' ').join(', '),
          location.customer_name,
          location.location_name,
          location.state,
          location.unit_count.toString(),
          formatCurrency(location.amount)
        ]);
        
        // Add subtotal row
        flatData.push([
          { content: 'SUBTOTAL', styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
          { content: '', styles: { fillColor: [240, 240, 240] } },
          { content: '', styles: { fillColor: [240, 240, 240] } },
          { content: '', styles: { fillColor: [240, 240, 240] } },
          { content: data.unitCount.toString(), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } },
          { content: formatCurrency(data.subtotal), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
        ]);
        
        autoTable(doc, {
          ...getTableStyles(),
          startY: currentY,
          head: [flatHeaders],
          body: flatData,
          columnStyles: {
            0: { cellWidth: 80 },
            1: { cellWidth: 100 },
            2: { cellWidth: 150 },
            3: { cellWidth: 60 },
            4: { cellWidth: 50 },
            5: { cellWidth: 80 }
          }
        });
      }
      
      currentY = doc.lastAutoTable.finalY + 20;
    });
    
    // Add grand total
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`GRAND TOTAL: ${formatCurrency(provider.total_amount)}`, 14, currentY);
    doc.setFont(undefined, 'normal');

    // Add footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      
      // Add page number
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 20,
        { align: 'center' }
      );
      
      // Add footer line
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(
        PDF_STYLES_CONSTANTS.margins.left,
        doc.internal.pageSize.height - 30,
        doc.internal.pageSize.width - PDF_STYLES_CONSTANTS.margins.right,
        doc.internal.pageSize.height - 30
      );
    }
    
    return doc;
  };

  // Add snackbar close handler
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If no resultId is provided, show the list of recent calculations
  if (!resultId) {
    return (
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h4">Recent Calculations</Typography>
            <Button
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={() => setBulkExportDialogOpen(true)}
            >
              Bulk Export
            </Button>
          </Box>
          <Typography paragraph>
            Select a calculation to view detailed results.
          </Typography>
          {renderRecentCalculations()}
          <BulkExportDialog open={bulkExportDialogOpen} onClose={() => setBulkExportDialogOpen(false)} />
          
          {/* Archive Confirmation Dialog */}
          <Dialog
            open={archiveDialogOpen}
            onClose={handleArchiveCancel}
          >
            <DialogTitle>Confirm Archive</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to archive "{fileToArchive?.filename}"? 
                It will be moved to the Archived tab and can be restored later.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleArchiveCancel}>Cancel</Button>
              <Button onClick={handleArchiveConfirm} color="warning">
                Archive
              </Button>
            </DialogActions>
          </Dialog>

          {/* Restore Confirmation Dialog */}
          <Dialog
            open={restoreDialogOpen}
            onClose={handleRestoreCancel}
          >
            <DialogTitle>Confirm Restore</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to restore "{fileToRestore?.filename}"? 
                It will appear in Recent Calculations.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleRestoreCancel}>Cancel</Button>
              <Button onClick={handleRestoreConfirm} color="primary">
                Restore
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>
    );
  }

  // Get the active provider's data
  const activeProviderData = allResults[activeProvider];
  console.log('Active provider index:', activeProvider);
  console.log('Active provider data:', activeProviderData);
  console.log('Active provider locations:', activeProviderData?.location_results);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">
            Calculation Results
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          
          <Chip 
            label={isRefusalCalculation ? "Refusal Work" : "Completed Work"} 
            color={isRefusalCalculation ? "secondary" : "primary"}
            sx={{ fontSize: '1rem', height: 32, mr: 3 }}
          />
          
          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={handleExportMenuOpen}
            disabled={allResults.length === 0}
            sx={{ mr: 2 }}
          >
            Export CSV
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
          >
            <MenuItem onClick={handleExportAllProviders}>
              <ListItemIcon>
                <GroupIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export all providers</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportEachProvider}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export each provider</ListItemText>
            </MenuItem>
          </Menu>
          
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PictureAsPdfIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={handlePdfMenuOpen}
            disabled={allResults.length === 0}
          >
            Export PDF
          </Button>
          <Menu
            anchorEl={pdfMenuAnchor}
            open={Boolean(pdfMenuAnchor)}
            onClose={handlePdfMenuClose}
          >
            <MenuItem onClick={handleExportAllProvidersPdf}>
              <ListItemIcon>
                <GroupIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export all providers</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportEachProviderPdf}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export each provider</ListItemText>
            </MenuItem>
          </Menu>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            onClick={handleEmailDialogOpen}
            disabled={allResults.length === 0}
            sx={{ ml: 1 }}
          >
            Export & Email
          </Button>
        </Box>
        
        {error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {error.startsWith('NO_RESULTS:') ? (
              // Special formatted display for "no calculation results" error
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3, 
                  maxWidth: 600, 
                  mx: 'auto', 
                  textAlign: 'left',
                  '& .MuiAlert-message': { width: '100%' }
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  No Calculation Results Found
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload ID: {error.replace('NO_RESULTS:', '')}...
                </Typography>
                
                <Typography variant="body2" sx={{ mt: 2, mb: 1 }} fontWeight="medium">
                  This usually means one of the following:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    The provider names in the uploaded file don't exactly match any <strong>active</strong> providers in the Rate Table
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    The providers may be marked as <strong>"Inactive"</strong> in the Rate Table
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    There may be invisible characters or extra spaces in the provider names
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  bgcolor: 'action.hover', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    💡 How to fix:
                  </Typography>
                  <Typography variant="body2">
                    1. Go to the <strong>Rate Table</strong> and check that the provider exists with the exact name<br/>
                    2. Verify the provider's status is set to <strong>"Active"</strong><br/>
                    3. Re-process the file to generate calculation results
                  </Typography>
                </Box>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 2 }}>
              {error.startsWith('NO_RESULTS:') && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/rate-table')}
                >
                  View Rate Table
                </Button>
              )}
              {showCalculateButton && resultId ? (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate(`/calculate/${resultId}`)}
                >
                  Re-Process This File
                </Button>
              ) : null}
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/upload-data')}
              >
                Go to Upload Page
              </Button>
            </Box>
          </Box>
        ) : allResults.length > 0 ? (
          <>
            {/* Summary Cards for all providers */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Amount (All Providers)
                    </Typography>
                    <Typography variant="h4" component="div">
                      {formatCurrency(totalAmount)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Locations
                    </Typography>
                    <Typography variant="h4" component="div">
                      {totalLocations}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Coca-Cola Revenue
                    </Typography>
                    <Typography variant="h4" component="div">
                      {formatCurrency(cocaColaRevenue)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderWidth: 2, 
                  borderStyle: 'solid',
                  borderColor: netProfit > 0 ? 'success.main' : 'error.main',
                  boxShadow: `0 0 8px ${netProfit > 0 ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`
                }}>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Gross Profit
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ color: netProfit > 0 ? 'success.main' : 'error.main' }}>
                      {formatCurrency(netProfit)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Units
                    </Typography>
                    <Typography variant="h4" component="div">
                      {totalBoards}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            {/* Service Type Summary - Only show if multiple service types exist */}
            {Object.keys(serviceTypeSummary).length > 1 && (
              <>
                <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2 }}>
                  Summary by Service Type
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Service Type</TableCell>
                        <TableCell align="center">Type</TableCell>
                        <TableCell align="right">Locations</TableCell>
                        <TableCell align="right">Units</TableCell>
                        {hasHourlyWork && <TableCell align="right">Billable Hours</TableCell>}
                        <TableCell align="right">Provider Cost</TableCell>
                        <TableCell align="right">Coca-Cola Revenue</TableCell>
                        <TableCell align="right">Gross Profit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(serviceTypeSummary).map(([serviceType, summary]) => (
                        <TableRow key={serviceType}>
                          <TableCell>
                            <Chip 
                              label={serviceType} 
                              size="small"
                              color={summary.rateType === 'HOURLY' ? 'secondary' : 'primary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">{summary.rateType}</TableCell>
                          <TableCell align="right">{summary.locationCount}</TableCell>
                          <TableCell align="right">{summary.unitCount}</TableCell>
                          {hasHourlyWork && (
                            <TableCell align="right">
                              {summary.billableHours > 0 ? summary.billableHours.toFixed(2) : '-'}
                            </TableCell>
                          )}
                          <TableCell align="right">{formatCurrency(summary.totalCost)}</TableCell>
                          <TableCell align="right">{formatCurrency(summary.revenue)}</TableCell>
                          <TableCell align="right" sx={{ 
                            color: (summary.revenue - summary.totalCost) > 0 ? 'success.main' : 'error.main',
                            fontWeight: 'bold'
                          }}>
                            {formatCurrency(summary.revenue - summary.totalCost)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: 2 } }}>
                        <TableCell>Total</TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="right">{totalLocations}</TableCell>
                        <TableCell align="right">{totalBoards}</TableCell>
                        {hasHourlyWork && (
                          <TableCell align="right">
                            {Object.values(serviceTypeSummary).reduce((sum, s) => sum + s.billableHours, 0).toFixed(2)}
                          </TableCell>
                        )}
                        <TableCell align="right">{formatCurrency(totalAmount)}</TableCell>
                        <TableCell align="right">{formatCurrency(cocaColaRevenue)}</TableCell>
                        <TableCell align="right" sx={{ 
                          color: netProfit > 0 ? 'success.main' : 'error.main'
                        }}>
                          {formatCurrency(netProfit)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ my: 3 }} />
              </>
            )}
            
            {/* Provider Tabs */}
            {allResults.length > 1 && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                  value={activeProvider} 
                  onChange={handleProviderChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {allResults.map((result, index) => (
                    <Tab 
                      key={result.id} 
                      label={`${result.provider_name} (${formatCurrency(result.total_amount)})`} 
                      id={`provider-tab-${index}`}
                    />
                  ))}
                </Tabs>
              </Box>
            )}
            
            {/* Provider Details */}
            {activeProviderData && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" gutterBottom>
                    {activeProviderData.provider_name}
                  </Typography>
                  
                  <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1">
                        <strong>Total Amount:</strong> {formatCurrency(activeProviderData.total_amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1">
                        <strong>Locations:</strong> {activeProviderData.total_locations}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1">
                        <strong>Units:</strong> {activeProviderData.total_boards}
                      </Typography>
                    </Grid>
                    {activeProviderData.net_terms && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body1">
                          <strong>Net Terms:</strong> Net {activeProviderData.net_terms}
                        </Typography>
                      </Grid>
                    )}
                    {activeProviderData.pay_date && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body1">
                          <strong>Pay Date:</strong> {format(new Date(activeProviderData.pay_date), 'MM/dd/yyyy')}
                        </Typography>
                      </Grid>
                    )}
                    {!activeProviderData.pay_date && activeProviderData.net_terms && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body1">
                          <strong>Pay Date:</strong> Net Terms not available
                        </Typography>
                      </Grid>
                    )}
                    {!activeProviderData.pay_date && !activeProviderData.net_terms && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body1">
                          <strong>Pay Date:</strong> Pay Date not found
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1">
                        <strong>Work Week:</strong> {activeProviderData.work_week_from_date ? 
                          format(getAdjustedDate(activeProviderData.work_week_from_date), 'MM/dd/yyyy') : 'Not set'} - 
                        {activeProviderData.work_week_to_date ? 
                          format(getAdjustedDate(activeProviderData.work_week_to_date), 'MM/dd/yyyy') : 'Not set'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 2 }}>
                  <Typography variant="h6">
                    Location Details ({activeProviderData.location_results.length} locations)
                  </Typography>
                </Box>
                <SortableTable
                  rows={activeProviderData.location_results}
                  headCells={[
                    { id: 'order_numbers', label: 'LogistixAI Order #', align: 'left' },
                    { id: 'customer_name', label: 'Customer Name', align: 'left' },
                    { id: 'location_name', label: 'Location', align: 'left' },
                    { id: 'state', label: 'State', align: 'left' },
                    { id: 'service_type_name', label: 'Service Type', align: 'left' },
                    { id: 'unit_count', label: 'Unit Count', align: 'right' },
                    ...(hasHourlyWork ? [
                      { id: 'travel_hours', label: 'Travel Hrs', align: 'right' },
                      { id: 'onsite_hours', label: 'Onsite Hrs', align: 'right' },
                      { id: 'billable_hours', label: 'Total Hrs', align: 'right' },
                    ] : []),
                    { id: 'amount', label: 'Amount', align: 'right' },
                  ]}
                  defaultOrderBy="location_name"
                  tableId="location-details-table"
                  defaultRowsPerPage={25}
                  renderRow={(location) => (
                    <TableRow 
                      key={location.id}
                      sx={location.is_excluded ? { 
                        backgroundColor: 'action.hover',
                        opacity: 0.8,
                        '& td': { color: 'text.secondary' }
                      } : {}}
                    >
                      <TableCell sx={{ maxWidth: 200, wordBreak: 'break-word' }}>
                        <Box>
                          {location.order_numbers ? (
                            <Typography variant="body2" sx={location.is_excluded ? { fontStyle: 'italic' } : {}}>
                              {location.order_numbers.split(' ').join(', ')}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                          {location.is_excluded && location.exclusion_reason && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={`Non-Payment: ${location.exclusion_reason}`}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            </Box>
                          )}
                          {/* Legacy excluded_orders display for backwards compatibility */}
                          {!location.is_excluded && location.excluded_orders && location.excluded_orders.trim() && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={`Excluded: ${location.excluded_orders.split(' ').join(', ')}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{location.customer_name}</TableCell>
                      <TableCell component="th" scope="row">
                        {location.location_name}
                      </TableCell>
                      <TableCell>{location.state}</TableCell>
                      <TableCell>
                        <Chip 
                          label={location.service_type_name} 
                          size="small"
                          color={location.is_excluded ? 'default' : (location.rate_type === 'HOURLY' ? 'secondary' : 'primary')}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{location.unit_count}</TableCell>
                      {hasHourlyWork && (
                        <>
                          <TableCell align="right">
                            {Number(location.travel_hours) > 0 ? 
                              Number(location.travel_hours).toFixed(2) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {Number(location.onsite_hours) > 0 ? 
                              Number(location.onsite_hours).toFixed(2) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {Number(location.billable_hours) > 0 ? 
                              Number(location.billable_hours).toFixed(2) : '-'}
                          </TableCell>
                        </>
                      )}
                      <TableCell align="right">
                        {location.is_excluded ? (
                          <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                            $0.00
                          </Typography>
                        ) : (
                          formatCurrency(location.amount)
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                />
              </>
            )}
          </>
        ) : (
          <Alert severity="info">No calculation results found.</Alert>
        )}
      </Paper>
      
      {/* Email Provider Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleEmailDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Send PDF Statement to Providers
        </DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            Select the providers you want to send emails to. Each selected provider will receive their own PDF statement via email.
            Providers without email addresses cannot be selected.
          </DialogContentText>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => handleSelectAllProviders(true)}
              disabled={sendingEmails}
            >
              Select All
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => handleSelectAllProviders(false)}
              disabled={sendingEmails}
            >
              Deselect All
            </Button>
          </Box>
          
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {allResults.map((provider) => (
              <ProviderEmailListItem 
                key={provider.provider_id} 
                provider={provider}
              />
            ))}
          </List>
          
          {emailResults && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Email Results:
              </Typography>
              <List dense>
                {emailResults.results.map((result, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {result.success ? (
                        <Typography color="success.main">✓</Typography>
                      ) : (
                        <Typography color="error.main">✗</Typography>
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={result.to}
                      secondary={result.success ? 'Sent successfully' : `Failed: ${result.message}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleEmailDialogClose} 
            disabled={sendingEmails}
          >
            Close
          </Button>
          <Button 
            onClick={handleSendEmails}
            variant="contained" 
            color="primary"
            disabled={sendingEmails || Object.keys(selectedProviders).filter(id => selectedProviders[id]).length === 0}
            startIcon={sendingEmails ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
          >
            {sendingEmails ? 'Sending...' : 'Send Emails'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Results; 